import { AcDomain } from 'api/interfaces/AcDomain'
import { defaultLogoStyle, defaultLogoStyleSquareImg } from 'features/planner/pages/MasterCampaignPlanPage/components/Projections/logoUtils'

export const getDomainByLogoUrl = (logoUrl: string, domains: AcDomain[]): AcDomain | null => {
  return domains.find(domain => domain.logoUrl === logoUrl) || null
}

export const applyDomainStyling = (domain: AcDomain | null) => {
  if (!domain) return { ...defaultLogoStyle }
  
  const logoStyle = { ...(domain.isSquare ? defaultLogoStyleSquareImg : defaultLogoStyle) }
  
  if (domain.maxWidth) {
    logoStyle.maxWidth = domain.maxWidth
  }
  
  if (domain.maxHeight) {
    logoStyle.maxHeight = domain.maxHeight
  }
  
  if (domain.domain === 'allure.com' || domain.domain === 'brakeforit.com' || domain.domain === 'buzzworthy.com') {
    logoStyle.backgroundColor = 'black'
    logoStyle.padding = '1px'
  }
  
  return logoStyle
}

export const calculateContainDimensions = (imgWidth: number, imgHeight: number, maxWidth: number, maxHeight: number) => {
  const aspectRatio = imgWidth / imgHeight
  const maxAspectRatio = maxWidth / maxHeight
  
  let width = maxWidth
  let height = maxHeight
  
  if (aspectRatio > maxAspectRatio) {
    height = maxWidth / aspectRatio
  } else {
    width = maxHeight * aspectRatio
  }
  
  return { width, height }
}

export const convertSvgToCanvas = async (img: HTMLImageElement, domain: AcDomain | null): Promise<HTMLCanvasElement | null> => {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    const logoStyle = applyDomainStyling(domain)
    const maxWidth = parseFloat(logoStyle.maxWidth?.toString() || '90')
    const maxHeight = parseFloat(logoStyle.maxHeight?.toString() || '20')
    
    const tempImg = new Image()
    tempImg.crossOrigin = 'anonymous'
    
    await new Promise((resolve, reject) => {
      tempImg.onload = () => resolve(void 0)
      tempImg.onerror = reject
      tempImg.src = img.src
    })
    
    const dimensions = calculateContainDimensions(
      tempImg.naturalWidth,
      tempImg.naturalHeight,
      maxWidth,
      maxHeight
    )
    
    canvas.width = dimensions.width * 2
    canvas.height = dimensions.height * 2
    canvas.style.width = `${dimensions.width}px`
    canvas.style.height = `${dimensions.height}px`
    canvas.style.objectFit = 'contain'
    
    if (logoStyle.backgroundColor) {
      canvas.style.backgroundColor = logoStyle.backgroundColor as string
    }
    if (logoStyle.padding) {
      canvas.style.padding = logoStyle.padding as string
    }
    
    ctx.scale(2, 2)
    
    if (logoStyle.backgroundColor) {
      ctx.fillStyle = logoStyle.backgroundColor as string
      ctx.fillRect(0, 0, dimensions.width, dimensions.height)
    }
    
    const padding = logoStyle.padding ? parseFloat(logoStyle.padding.toString()) : 0
    ctx.drawImage(
      tempImg, 
      padding, 
      padding, 
      dimensions.width - 2 * padding, 
      dimensions.height - 2 * padding
    )

    return canvas
  } catch (error) {
    return null
  }
}

export const processSvgImagesInElement = async (element: HTMLElement, domains: AcDomain[]): Promise<void> => {
  const imgElements = element.querySelectorAll('img')
  const svgConversions: Promise<void>[] = []
  
  imgElements.forEach((img: HTMLImageElement) => {
    if (img.src && (img.src.endsWith('.svg') || img.src.includes('image/svg'))) {
      const domain = getDomainByLogoUrl(img.src, domains)
      svgConversions.push(
        convertSvgToCanvas(img, domain).then(canvas => {
          if (canvas && img.parentNode) {
            canvas.className = img.className
            img.parentNode.replaceChild(canvas, img)
          }
        }).catch(() => {})
      )
    }
  })
  
  await Promise.all(svgConversions)
}
